// supabaseClient.js
// One shared Supabase client for the whole app.
// The two values come from Vercel Environment Variables (see setup guide, Part 4/5b).
import { createClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL;
const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anon) {
  // Helpful console message if the env vars are missing
  console.error(
    "Missing Supabase env vars. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in Vercel."
  );
}

const realClient = createClient(url, anon);

/* ------------------------------------------------------------
   READ-ONLY (PREVIEW) MODE

   While a manager is using "View as employee", every write is blocked
   here, at the single place all database traffic passes through. Reads
   work normally; inserts, updates, upserts, deletes, file uploads and
   edge-function calls return an error instead of reaching Supabase. This
   guarantees a preview can never sign, submit, log a view, or change
   anything on the employee's behalf, whatever button gets clicked.
   ------------------------------------------------------------ */
let readOnly = false;
export function setReadOnly(on) { readOnly = !!on; }
export function isReadOnly() { return readOnly; }

const PREVIEW_ERROR = {
  message: "Preview mode — nothing is saved while viewing as an employee.",
  code: "PREVIEW_READ_ONLY",
};

// A query builder stand-in: every chained call (.select(), .eq(), .single()…)
// returns itself, and awaiting it resolves to the preview error.
function blockedQuery() {
  const p = new Proxy(function () {}, {
    get(_t, prop) {
      if (prop === "then") {
        return (resolve, reject) =>
          Promise.resolve({ data: null, error: PREVIEW_ERROR, count: null, status: 403 }).then(resolve, reject);
      }
      return () => p;
    },
    apply() { return p; },
  });
  return p;
}

const TABLE_WRITES = new Set(["insert", "update", "upsert", "delete"]);
const STORAGE_WRITES = new Set(["upload", "update", "remove", "move", "copy"]);
const bindOf = (obj, key) => {
  const v = obj[key];
  return typeof v === "function" ? v.bind(obj) : v;
};

export const supabase = new Proxy(realClient, {
  get(target, prop) {
    if (prop === "from") {
      return (table) => {
        const qb = target.from(table);
        return new Proxy(qb, {
          get(t, m) {
            if (readOnly && TABLE_WRITES.has(m)) return () => blockedQuery();
            return bindOf(t, m);
          },
        });
      };
    }
    if (prop === "rpc") {
      return (...args) => (readOnly ? blockedQuery() : target.rpc(...args));
    }
    if (prop === "functions") {
      if (!readOnly) return target.functions;
      return { invoke: async () => ({ data: null, error: PREVIEW_ERROR }) };
    }
    if (prop === "storage") {
      const st = target.storage;
      return new Proxy(st, {
        get(s, k) {
          if (k !== "from") return bindOf(s, k);
          return (bucket) => {
            const b = s.from(bucket);
            return new Proxy(b, {
              get(bb, m) {
                if (readOnly && STORAGE_WRITES.has(m)) return async () => ({ data: null, error: PREVIEW_ERROR });
                return bindOf(bb, m);
              },
            });
          };
        },
      });
    }
    return bindOf(target, prop);
  },
});
